import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ArrowDownIcon, HomeIcon, ClipboardListIcon, FileTextIcon, SparklesIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trackEvent } from '../../utils/analytics';
import { PERMIS_IMAGES } from '../../data/permisHero';
import { permisCtaCopy, permisCtaSteps, PERMIS_CTA_IMAGE, type Bi } from '../../data/permisFinalCta';
import { GreenCheck } from './PermisCards';
const EASE = [0.23, 1, 0.32, 1] as const;
// Exact logo files used in the site header (components/Header.tsx)
const BCITI_LOGO = 'https://cdn.magicpatterns.com/uploads/kFQruRR3oauw7NNUdgFajn/Logo_BCITI_(5).png';
const ATNA_LOGO = 'https://cdn.magicpatterns.com/uploads/d6cqpG2SGUofNqC1hEedXv/Logo_ATNA_(5).png';
const STEP_ICONS = {
  property: HomeIcon,
  rules: ClipboardListIcon,
  permit: FileTextIcon
} as const;
const CARD = 'rounded-[22px] border border-[#ECECF6] bg-white shadow-[0_24px_50px_-34px_rgba(40,36,120,0.45)]';
export function PermisFinalCTA() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  const c = permisCtaCopy;
  const reveal = {
    initial: {
      opacity: 0,
      y: 12
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-80px'
    }
  };
  const buttons = <div className="flex flex-col sm:flex-row gap-3 sm:gap-4" data-id="element-5010" data-mp="e3i1ymikkqop">
      <Link to="/request-demo" onClick={() => trackEvent('CTA Clicked', {
      location: 'permis_final',
      cta: 'see_in_action'
    })} className="inline-flex items-center justify-center gap-2.5 rounded-full bg-[#4F46E5] px-8 py-4 font-sans text-[16px] font-medium text-white whitespace-nowrap shadow-[0_14px_30px_-14px_rgba(79,70,229,0.7)] hover:bg-[#4338CA] transition-colors duration-150" data-id="element-5011" data-mp="rv6dankzvb4m">
        {pick(c.primary)}
        <ArrowRightIcon className="w-4 h-4" strokeWidth={2.2} aria-hidden="true" data-id="element-5012" data-mp="2yn76vrnpxnrq" />
      </Link>
      <Link to="/request-demo" onClick={() => trackEvent('CTA Clicked', {
      location: 'permis_final',
      cta: 'talk_to_team'
    })} className="inline-flex items-center justify-center rounded-full border-[1.5px] border-[#4F46E5] bg-white px-8 py-4 font-sans text-[16px] font-medium text-[#4F46E5] whitespace-nowrap hover:bg-[#F5F4FE] transition-colors duration-150" data-id="element-5013" data-mp="2ypjuolg5qscp">
        {pick(c.secondary)}
      </Link>
    </div>;
  return <div className="relative max-w-[1640px] mx-auto" data-id="element-5014" data-mp="b41helqovkkd">
      <div className="grid grid-cols-1 lg:grid-cols-[40fr_60fr] gap-10 lg:gap-8 items-center" data-id="element-5015" data-mp="f66759e0nx1q">
        {/* Left — editorial */}
        <motion.div {...reveal} transition={{
        duration: 0.3,
        ease: EASE
      }} className="min-w-0" data-id="element-5016" data-mp="jy618l7envqe">
          <p className="font-sans text-[13px] font-semibold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-5017" data-mp="owhe74szymcu">{pick(c.eyebrow)}</p>
          <h2 className="font-serif text-[2.6rem] sm:text-[3.4rem] 2xl:text-[4.2rem] leading-[1.04] tracking-tight mt-5" data-id="element-5018" data-mp="twvkoiey9o8r">
            <span className="block text-[#121A3A]" data-id="element-5019" data-mp="ysayjacyjk56">{pick(c.line1)}</span>
            <span className="block text-[#121A3A]" data-id="element-5020" data-mp="2je0w03kayf78">{pick(c.line2)}</span>
            <span className="block text-[#4F46E5]" data-id="element-5021" data-mp="2ed3dtqzall5q">{pick(c.line3)}</span>
            <span className="block text-[#4F46E5]" data-id="element-5022" data-mp="29e9x7pople1c">{pick(c.line4)}</span>
          </h2>
          <p className="font-sans text-[18px] md:text-[20px] text-[#5B6478] leading-relaxed mt-7 max-w-[540px]" data-id="element-5023" data-mp="29g37deyj6ge3">
            {pick(c.bodyBefore)}
            <strong className="font-semibold text-[#121A3A]" data-id="element-5024" data-mp="321w4b7x5b0sy">{pick(c.bodyLead)}</strong>
            {pick(c.bodyAfter)}
          </p>
          <div className="hidden lg:block mt-9" data-id="element-5025" data-mp="2x4me3q1xml42">{buttons}</div>
        </motion.div>

        {/* Right — one conversation */}
        <motion.div {...reveal} transition={{
        duration: 0.3,
        delay: 0.05,
        ease: EASE
      }} className="relative min-w-0" data-id="element-5026" data-mp="2ne18hinucgbn">
          <img src={PERMIS_CTA_IMAGE} alt="" aria-hidden="true" className="absolute -top-16 -right-6 w-[78%] h-[calc(100%+64px)] object-cover object-[70%_50%] opacity-95" style={{
          WebkitMaskImage: 'linear-gradient(to right, transparent 0%, #000 40%), linear-gradient(to bottom, #000 65%, transparent 100%)',
          WebkitMaskComposite: 'source-in',
          maskImage: 'linear-gradient(to right, transparent 0%, #000 40%), linear-gradient(to bottom, #000 65%, transparent 100%)',
          maskComposite: 'intersect'
        }} data-id="element-5027" data-mp="3ktyp8vgtl390" />

          <div className="relative space-y-5 lg:pr-4" data-id="element-5028" data-mp="3jboy3hnydfyi">
            {/* Citizen question */}
            <div className={`${CARD} rounded-full flex items-center gap-4 sm:gap-6 p-2.5 pr-6 sm:pr-10 max-w-[620px]`} data-id="element-5029" data-mp="3o8yl6qnvdfj6">
              <img src={PERMIS_IMAGES.avatar} alt="" className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover shrink-0" data-id="element-5030" data-mp="39l3sha9v3b63" />
              <p className="font-sans text-[15px] sm:text-[18px] font-medium text-[#121A3A] leading-snug" data-id="element-5031" data-mp="64n7s5odqh3r">{pick(c.question)}</p>
            </div>

            {/* ATNA answer */}
            <div className={`${CARD} flex items-start gap-4 sm:gap-5 p-5 sm:p-6 max-w-[680px] lg:ml-6`} data-id="element-5032" data-mp="axgc4sfxrnfb">
              <span className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-5033" data-mp="1vjfpuhiixb9v">
                <SparklesIcon className="w-7 h-7 text-[#4F46E5] fill-[#4F46E5]" strokeWidth={1.5} data-id="element-5034" data-mp="25ak4fg4aeu46" />
              </span>
              <div className="min-w-0" data-id="element-5035" data-mp="20d0simv2atfe">
                <p className="font-sans text-[17px] sm:text-[19px] font-semibold text-[#121A3A]" data-id="element-5036" data-mp="1cmwq2itm0zpz">ATNA</p>
                <p className="font-sans text-[15px] sm:text-[18px] text-[#5B6478] leading-snug mt-1" data-id="element-5037" data-mp="17o39ghj10sll">{pick(c.answer)}</p>
              </div>
            </div>

            {/* Three verified steps */}
            <ol className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr_auto_1fr] items-stretch gap-3 lg:ml-6" data-id="element-5038" data-mp="1hm9a6vt3jsov">
              {permisCtaSteps.map((s, i) => {
              const Icon = STEP_ICONS[s.key];
              return <Fragment key={s.key} data-id="element-5039" data-mp="2t04qw8zng9i7" data-mpi={i}>
                    <li className={`${CARD} relative flex flex-col items-center text-center px-4 pt-5 pb-5`} data-id="element-5040" data-mp="2t3941vv3efl0">
                      <span className="absolute top-4 right-4" data-id="element-5041" data-mp="1upd8tufr826n">
                        <GreenCheck size={26} data-id="element-5042" data-mp="1unseo5xcql9b" />
                      </span>
                      <span className="w-16 h-16 rounded-full bg-[#F1F0FD] flex items-center justify-center" aria-hidden="true" data-id="element-5043" data-mp="1zphqvowb94yz">
                        <Icon className="w-7 h-7 text-[#4F46E5]" strokeWidth={1.7} data-id="element-5044" data-mp="23p8nwacfkhby" />
                      </span>
                      <span className="mt-4 font-sans text-[16px] font-semibold text-[#121A3A]" data-id="element-5045" data-mp="16xods6bjszm0">{pick(s.title)}</span>
                      <span className="mt-1.5 font-sans text-[14px] text-[#5B6478] leading-snug max-w-[190px]" data-id="element-5046" data-mp="1g0h4o8gtj9bb">{pick(s.body)}</span>
                    </li>
                    {i < permisCtaSteps.length - 1 && <li className="flex items-center justify-center" aria-hidden="true" data-id="element-5047" data-mp="o9t34vb8g1x4">
                        <ArrowRightIcon className="hidden sm:block w-5 h-5 text-[#7C74EE]" strokeWidth={1.8} data-id="element-5048" data-mp="samtrwg27utp" />
                        <ArrowDownIcon className="sm:hidden w-5 h-5 text-[#7C74EE]" strokeWidth={1.8} data-id="element-5049" data-mp="krvm3fczj2jy" />
                      </li>}
                  </Fragment>;
            })}
            </ol>
          </div>
        </motion.div>

        <div className="lg:hidden" data-id="element-5050" data-mp="bsic8lky8byp">{buttons}</div>
      </div>

      {/* Brand signature — same logo files as the header */}
      <div className="mt-16 lg:mt-20 flex flex-wrap items-center justify-center gap-x-4 gap-y-2" data-id="element-5051" data-mp="1s7zf22tt6k2">
        <span className="flex items-center" data-id="element-5052" data-mp="3t7fbu4eg62u9">
          <img src={BCITI_LOGO} alt="BCITI" className="h-[4.2rem] w-auto object-contain" data-id="element-5053" data-mp="3t5m56p3u3im9" />
          <span className="ml-3 mr-1 text-[#121212]/25 font-sans text-2xl font-light select-none" aria-hidden="true" data-id="element-5054" data-mp="3o8vr27blax3h">|</span>
          <img src={ATNA_LOGO} alt="ATNA" className="h-[3.6rem] w-auto object-contain" data-id="element-5055" data-mp="3k4e6i5xeofrn" />
        </span>
        <span className="hidden sm:block w-px h-8 bg-[#121A3A]/20" aria-hidden="true" data-id="element-5056" data-mp="3f9gxv041g8fa" />
        <span className="font-serif text-[18px] sm:text-[20px] text-[#121A3A]" data-id="element-5057" data-mp="3a930qd3z6iw1">{pick(c.tagline)}</span>
      </div>
    </div>;
}